import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import CategoryMissionService from "../../../services/CategoryMissionService";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import Message from "../../tools/Message";
import Title from "../../../components/Title";
import useConfirm from "../../../hooks/useConfirm";

const FILTRES_INITIAUX = {
  direction: "",
  typeEnvoi: "",
};

const useStyles = makeStyles((theme) => ({
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid #dbe3ef",
    borderRadius: 10,
    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
  },
  filterLabel: {
    display: "block",
    marginBottom: 5,
    color: "#475569",
    fontSize: 13,
    fontWeight: 600,
  },
  tableCard: {
    padding: theme.spacing(2),
    border: "1px solid #dbe3ef",
    borderRadius: 10,
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  resultCount: {
    color: "#64748b",
    fontSize: 13,
  },
  gridWrapper: {
    width: "100%",
    height: 640,
  },
  dataGrid: {
    border: "0 !important",
    color: "#334155",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f4f7fb",
      borderBottom: "1px solid #dce6f3",
      color: "#29476f",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      whiteSpace: "normal",
      lineHeight: 1.25,
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "#f8fbff",
    },
    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
      paddingTop: 8,
      paddingBottom: 8,
      borderColor: "#edf1f7",
      whiteSpace: "normal",
      lineHeight: 1.4,
    },
    "& .MuiDataGrid-cellContent": {
      width: "100%",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "1px solid #dce6f3",
    },
  },
  copies: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    width: "100%",
    padding: "6px 0",
  },
}));

const valeursUniques = (rows, field) =>
  [...new Set(rows.map((row) => row[field]).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b), "fr", { sensitivity: "base" }),
  );

export default function CopieMailTable() {
  const navigate = useNavigate();
  const classes = useStyles();
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const [rows, setRows] = useState([]);
  const [filtres, setFiltres] = useState(FILTRES_INITIAUX);
  const [filtresAppliques, setFiltresAppliques] = useState(FILTRES_INITIAUX);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    color: "green",
    messageInfo: "",
  });

  const chargerConfigurations = useCallback(async () => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      const result = await CategoryMissionService.listcopie();
      const data = Array.isArray(result?.data) ? result.data : [];
      setRows(data.map((row) => ({ ...row, id: row.mail_int })));

      if (result && result.status === false) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: result.message || "Erreur lors du chargement des configurations.",
        });
      }
    } catch (error) {
      setRows([]);
      setMessage({
        open: true,
        color: "red",
        messageInfo: "Erreur lors du chargement des configurations.",
      });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  }, []);

  useEffect(() => {
    chargerConfigurations();
  }, [chargerConfigurations]);

  const directions = useMemo(() => valeursUniques(rows, "direction_miss"), [rows]);
  const typesEnvoi = useMemo(() => valeursUniques(rows, "type"), [rows]);

  const lignesFiltrees = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!filtresAppliques.direction ||
            row.direction_miss === filtresAppliques.direction) &&
          (!filtresAppliques.typeEnvoi || row.type === filtresAppliques.typeEnvoi),
      ),
    [filtresAppliques, rows],
  );

  const handleEdit = useCallback(
    (id) => navigate(`/mission_mail/add/${id}`),
    [navigate],
  );

  const handleDelete = useCallback(
    async (id) => {
      setOpenLoadingSimpleBackdrop(true);
      try {
        const result = await CategoryMissionService.deletecopie(id);
        if (!result?.status) {
          setMessage({
            open: true,
            color: "red",
            messageInfo: result?.message || "La suppression a échoué.",
          });
          return;
        }

        setMessage({
          open: true,
          color: "green",
          messageInfo: result.message || "Configuration supprimée avec succès.",
        });
        await chargerConfigurations();
      } catch (error) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: "Erreur lors de la suppression de la configuration.",
        });
      } finally {
        setOpenLoadingSimpleBackdrop(false);
      }
    },
    [chargerConfigurations],
  );

  const handleConfirmDelete = useCallback(
    (id) => {
      openConfirmDialog(
        "Confirmation de suppression",
        "Êtes-vous sûr de vouloir supprimer cette configuration ?",
        () => handleDelete(id),
      );
    },
    [handleDelete, openConfirmDialog],
  );

  const columns = useMemo(
    () => [
      {
        field: "type",
        headerName: "Type d'envoi",
        minWidth: 220,
        flex: 0.9,
        renderCell: (params) => (
          <Chip
            label={params.value || "—"}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ maxWidth: "100%", fontWeight: 600 }}
          />
        ),
      },
      {
        field: "direction_miss",
        headerName: "Direction des missionnaires",
        minWidth: 220,
        flex: 0.9,
      },
      {
        field: "service_miss",
        headerName: "Service des missionnaires",
        minWidth: 210,
        flex: 0.8,
        valueFormatter: (value) => value || "—",
      },
      {
        field: "copie",
        headerName: "Utilisateurs en copie",
        minWidth: 320,
        flex: 1.3,
        sortable: false,
        renderCell: (params) => {
          const copies = Array.isArray(params.value) ? params.value : [];
          return copies.length > 0 ? (
            <Box className={classes.copies}>
              {copies.map((copie, index) => (
                <Chip
                  key={`${params.row.id}-${copie.user_id || copie.name}-${index}`}
                  label={copie.name || "—"}
                  size="small"
                  sx={{ backgroundColor: "#eef2f7", color: "#475569" }}
                />
              ))}
            </Box>
          ) : (
            "Aucun utilisateur"
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEdit(params.row.id)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleConfirmDelete(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [classes.copies, handleConfirmDelete, handleEdit],
  );

  const appliquerFiltres = () => setFiltresAppliques({ ...filtres });

  const reinitialiserFiltres = () => {
    setFiltres(FILTRES_INITIAUX);
    setFiltresAppliques(FILTRES_INITIAUX);
  };

  return (
    <>
      <DialogComponent />
      <Message
        open={message.open}
        color={message.color}
        messageInfo={message.messageInfo}
        handleClose={() => setMessage((current) => ({ ...current, open: false }))}
      />
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

      <Paper className={classes.filterCard} variant="outlined">
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <label className={classes.filterLabel}>Direction des missionnaires</label>
            <Autocomplete
              options={directions}
              value={filtres.direction || null}
              onChange={(_event, value) =>
                setFiltres((current) => ({ ...current, direction: value || "" }))
              }
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Toutes les directions" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <label className={classes.filterLabel}>Type d'envoi</label>
            <Autocomplete
              options={typesEnvoi}
              value={filtres.typeEnvoi || null}
              onChange={(_event, value) =>
                setFiltres((current) => ({ ...current, typeEnvoi: value || "" }))
              }
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Tous les types" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={reinitialiserFiltres}
              >
                Réinitialiser
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={appliquerFiltres}
              >
                Filtrer
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.tableCard} variant="outlined">
        <Box className={classes.tableHeader}>
          <Title>Liste des utilisateurs en copie de mail</Title>
          <Box className={classes.resultCount}>
            {lignesFiltrees.length} configuration{lignesFiltrees.length > 1 ? "s" : ""}
          </Box>
        </Box>
        <Box className={classes.gridWrapper}>
          <DataGrid
            rows={lignesFiltrees}
            columns={columns}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            className={classes.dataGrid}
            localeText={{ noRowsLabel: "Aucune configuration trouvée" }}
          />
        </Box>
      </Paper>
    </>
  );
}
